import React, { useState, useEffect } from "react";
import { Button, Col, Modal, Row, Table } from "reactstrap";

/**
 * FlexCredForm - Modal form for adding/editing flexible credentials
 * Only used when appointment system has cred_fields defined
 */
function FlexCredForm({ isOpen, toggle, credFields, initialValue, onSave, isEditing, existingGroupNames = [], hasDefaultCred = false }) {
  const [form, setForm] = useState({});
  const [originalPasswords, setOriginalPasswords] = useState({});
  const [originalFormValues, setOriginalFormValues] = useState({});

  useEffect(() => {
    if (isEditing && initialValue) {
      // When editing existing credential:
      // 1. Store masked passwords separately
      // 2. Clear password fields so user sees empty input with placeholder
      // 3. Derive and set cred_type so it persists correctly on save
      const passwords = {};
      const formData = { ...initialValue };

      // Derive cred_type from group_name if not already set
      if (!formData.cred_type) {
        formData.cred_type = formData.group_name && formData.group_name !== "default"
          ? "GROUP"
          : "DEFAULT";
      }

      credFields.forEach(field => {
        if (field.type === "password" && initialValue[field.name]) {
          passwords[field.name] = initialValue[field.name]; // Store the masked value like "f****hs"
          formData[field.name] = ""; // Clear for editing
        }
      });

      setOriginalPasswords(passwords);
      setOriginalFormValues({ ...formData }); // Store original for comparison
      setForm(formData);
    } else {
      // When creating new credential, just use initial values
      setForm(initialValue || {});
      setOriginalPasswords({});
      setOriginalFormValues({});
    }
  }, [initialValue, isOpen, isEditing, credFields]);

  // Check if form has changes (for editing) or has required fields filled (for adding)
  const hasChanges = () => {
    if (!isEditing) {
      // For new credentials, check if at least one non-password field has a value
      return credFields.some(field => {
        const value = form?.[field.name];
        return value && value.toString().trim() !== "";
      });
    }

    // For editing, check if any field changed from original
    for (const field of credFields) {
      const currentVal = form?.[field.name] ?? "";
      const originalVal = originalFormValues?.[field.name] ?? "";
      if (currentVal !== originalVal) {
        return true;
      }
    }
    return false;
  };

  const setField = (name, value) => {
    setForm(prev => ({ ...(prev || {}), [name]: value }));
  };

  const credType = form?.cred_type ||
    (form?.group_name && form.group_name !== "default" ? "GROUP" : "DEFAULT");

  // Check if group name already exists (for new groups or when adding)
  const isDuplicateGroupName = () => {
    if (credType === "GROUP") {
      const groupName = form?.group_name?.trim();
      if (!groupName) return false;
      return existingGroupNames.includes(groupName);
    }
    // For DEFAULT type, check if default already exists (only when adding new)
    if (credType === "DEFAULT" && !isEditing && hasDefaultCred) {
      return true;
    }
    return false;
  };

  const handleSave = () => {
    const finalType = form?.cred_type || "DEFAULT";
    const final = {
      ...form,
      group_name: finalType === "GROUP" ? (form.group_name || "") : "default",
    };

    // Handle passwords: if field is empty and we have original, keep the masked original
    // Mark it so transform function knows it's a masked password
    credFields.forEach(field => {
      if (field.type === "password") {
        if (!final[field.name] && originalPasswords[field.name]) {
          // User didn't enter new password, keep the masked one with a marker
          final[field.name] = originalPasswords[field.name];
          final[`__masked_${field.name}`] = true; // Marker for transform function
        }
        // If user entered something, send it as-is (new password)
      }
    });

    delete final.cred_type; // Don't persist UI-only field
    onSave(final);
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <div className="p-3">
        <div className="fw-bold mb-3">
          {isEditing ? "Edit Credential" : "Add Credential"}
        </div>

        {/* Credential Type Selection */}
        <div className="mb-3">
          <div className="form-label">Credential Type</div>
          <select
            className={`form-control ${credType === "DEFAULT" && !isEditing && hasDefaultCred ? "is-invalid" : ""}`}
            value={credType}
            onChange={(e) => setField("cred_type", e.target.value)}
            disabled={isEditing}
          >
            <option value="DEFAULT">Default</option>
            <option value="GROUP">Group</option>
          </select>
          {credType === "DEFAULT" && !isEditing && hasDefaultCred && (
            <div className="invalid-feedback">
              Default credential already exists
            </div>
          )}
        </div>

        {/* Group Name (only for GROUP type) */}
        {credType === "GROUP" && (
          <div className="mb-3">
            <div className="form-label">
              Group Name
              {isEditing && (
                <span className="text-muted ms-2" style={{ fontSize: "0.85em" }}>
                  (Cannot change - delete and create new group instead)
                </span>
              )}
            </div>
            <input
              className={`form-control ${isDuplicateGroupName() ? "is-invalid" : ""}`}
              value={form?.group_name ?? ""}
              onChange={(e) => setField("group_name", e.target.value)}
              placeholder="e.g. teamA"
              disabled={isEditing}
            />
            {isDuplicateGroupName() && (
              <div className="invalid-feedback">
                Group name already exists
              </div>
            )}
          </div>
        )}

        {/* Dynamic credential fields */}
        {credFields.map(field => {
          const isPasswordField = field.type === "password";
          const hasOriginalPassword = isPasswordField && originalPasswords[field.name];
          const currentValue = form?.[field.name] ?? "";

          return (
            <div key={field.name} className="mb-3">
              <div className="form-label">
                {field.label || field.name}
                {hasOriginalPassword && (
                  <span className="text-muted ms-2" style={{ fontSize: "0.85em" }}>
                    (Leave empty to keep current)
                  </span>
                )}
              </div>
              <input
                className="form-control"
                type="text"
                value={currentValue}
                placeholder={
                  hasOriginalPassword
                    ? originalPasswords[field.name]
                    : (field.placeholder || "")
                }
                onChange={e => setField(field.name, e.target.value)}
              />
            </div>
          );
        })}

        <div className="d-flex gap-2 justify-content-end">
          <Button color="secondary" onClick={toggle}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleSave} disabled={!hasChanges() || isDuplicateGroupName()}>
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * GroupFlexCred - Main component for managing flexible credentials
 * ONLY used when appointment system has cred_fields defined
 * Does NOT interfere with existing userId/password GroupCred component
 */
export default function GroupFlexCred({
  creds = [],
  credFields = [],
  isEdit = true,
  setCreds,
  updateCreds,
  setUpdateCreds,
  deleteCreds,
  setDeleteCreds,
}) {
  const [editingIndex, setEditingIndex] = useState(null);
  const [showForm, setShowForm] = useState(false);

  // Mask password values for display in table
  const displayValue = (field, value) => {
    if (value == null) return "";
    const str = String(value);

    // If it's a password field, show masked
    if (field.type === "password") {
      if (str.length <= 4) return str;
      return `${str.slice(0, 1)}${"*".repeat(str.length - 3)}${str.slice(-2)}`;
    }

    return str;
  };

  const openAdd = () => {
    setEditingIndex(null);
    setShowForm(true);
  };

  const openEdit = (idx) => {
    setEditingIndex(idx);
    setShowForm(true);
  };

  const handleDelete = (idx) => {
    const row = creds[idx];
    const nextCreds = creds.filter((_, i) => i !== idx);

    setCreds?.(nextCreds);

    // Track deletion by group_name
    const groupName = row?.group_name;
    if (groupName) {
      setDeleteCreds?.([...(deleteCreds || []), groupName]);
    }
  };

  const handleSave = (val) => {
    const nextCreds = [...(creds || [])];

    if (editingIndex != null) {
      // Editing existing credential
      const oldRow = nextCreds[editingIndex];
      const oldGroupName = oldRow?.group_name;
      const newGroupName = val?.group_name;

      // If group_name changed, track old one for deletion
      if (oldGroupName && newGroupName && oldGroupName !== newGroupName) {
        setDeleteCreds?.([...(deleteCreds || []), oldGroupName]);
      }

      nextCreds[editingIndex] = val;
    } else {
      // Adding new credential
      nextCreds.push(val);
    }

    setCreds?.(nextCreds);
    setUpdateCreds?.([...(updateCreds || []), val]);
    setEditingIndex(null);
  };

  const editingValue = editingIndex != null ? creds[editingIndex] : null;

  // Get existing group names for duplicate validation (exclude current editing group)
  const existingGroupNames = (creds || [])
    .map(c => c.group_name)
    .filter(name => name && name !== "default")
    .filter(name => editingIndex == null || name !== creds[editingIndex]?.group_name);

  // Check if default credential already exists
  const hasDefaultCred = (creds || []).some(c => c.group_name === "default");

  const credentialTypeLabel = (row) =>
    row?.group_name && row.group_name !== "default" ? "Group" : "Default";

  return (
    <>
      {/* Only render modal when in edit mode */}
      {isEdit && (
        <FlexCredForm
          isOpen={showForm}
          toggle={() => setShowForm(v => !v)}
          credFields={credFields}
          initialValue={editingValue}
          onSave={handleSave}
          isEditing={editingIndex !== null}
          existingGroupNames={existingGroupNames}
          hasDefaultCred={hasDefaultCred}
        />
      )}

      <Row>
        <Col md={4}>
          <span className="text-muted">Credentials:</span>
        </Col>

        <Col md={8}>
          {creds?.length > 0 ? (
            <Table>
              <thead>
                <tr>
                  <th style={{ border: "none" }}>Credential Type</th>
                  <th style={{ border: "none" }}>Group Name</th>
                  {credFields.map(field => (
                    <th key={field.name} style={{ border: "none" }}>
                      {field.label || field.name}
                    </th>
                  ))}
                  {isEdit && <th style={{ border: "none" }}>Action</th>}
                </tr>
              </thead>

              <tbody>
                {creds.map((row, idx) => (
                  <tr key={idx}>
                    <td style={{ border: "none" }}>{credentialTypeLabel(row)}</td>
                    <td style={{ border: "none" }}>{row?.group_name || "default"}</td>
                    {credFields.map(field => (
                      <td key={field.name} style={{ border: "none" }}>
                        {displayValue(field, row?.[field.name])}
                      </td>
                    ))}
                    {isEdit && (
                      <td style={{ border: "none" }}>
                        <i
                          className="mdi mdi-pencil font-size-16 text-success me-2 pointer"
                          onClick={() => openEdit(idx)}
                        />
                        <i
                          className="mdi mdi-trash-can font-size-16 text-danger pointer"
                          onClick={() => handleDelete(idx)}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="text-muted mb-2">No credentials yet.</div>
          )}

          {isEdit && (
            <Button color="primary" className="btn btn-sm mt-2" onClick={openAdd}>
              Add
            </Button>
          )}
        </Col>
      </Row>
    </>
  );
}
